/** Organization datagrid key base — full key is `${USERS_DATAGRID_KEY}_${organizationId}` */
export const USERS_DATAGRID_KEY = "organization_users_grid";

export const USER_DEFAULT_COLUMNS = [
  {
    field: "application_user_full_name",
    label: "Name",
    visible: true,
    width: 180,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "application_user_email",
    label: "Email",
    visible: true,
    width: 200,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "application_user_phone",
    label: "Phone",
    visible: true,
    width: 140,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "user_type_name",
    label: "User Type",
    visible: true,
    width: 120,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "application_user_role_id_name",
    label: "Role",
    visible: true,
    width: 140,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "is_active",
    label: "Active",
    visible: true,
    width: 100,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
];

export const USER_PERSONALIZE_CONFIG = {
  defaultVisibleColumns: USER_DEFAULT_COLUMNS.map((col) => col.field),
  columnLabels: Object.fromEntries(
    USER_DEFAULT_COLUMNS.map((col) => [col.field, col.label])
  ),
  columnWidths: Object.fromEntries(
    USER_DEFAULT_COLUMNS.map((col) => [col.field, col.width])
  ),
};

export const USER_PERSONALIZE_COLUMN_KEYS = USER_DEFAULT_COLUMNS.map(
  (col) => col.field
);

const COLUMN_FIELD_ALIASES = {
  user_name: "application_user_full_name",
  full_name: "application_user_full_name",
  name: "application_user_full_name",
  email: "application_user_email",
  phone: "application_user_phone",
  phone_number: "application_user_phone",
  user_type: "user_type_name",
  user_role: "application_user_role_id_name",
  application_user_role: "application_user_role_id_name",
  user_role_name: "application_user_role_id_name",
};

const METADATA_FILTER_FIELDS = new Set([
  "organization",
  "organization_entity",
  "application_user",
  "user_type",
  "is_active",
]);

export function normalizeUserColumnField(field) {
  const raw = String(field || "").trim();
  if (!raw) return raw;
  if (COLUMN_FIELD_ALIASES[raw]) return COLUMN_FIELD_ALIASES[raw];
  const lower = raw.toLowerCase();
  return COLUMN_FIELD_ALIASES[lower] || raw;
}

export function resolveUserApiField(field) {
  const raw = String(field || "").trim();
  if (METADATA_FILTER_FIELDS.has(raw)) return raw;
  return normalizeUserColumnField(field);
}

export function mapSavedUserColumns(savedColumns = []) {
  const labelByField = USER_PERSONALIZE_CONFIG.columnLabels || {};

  return savedColumns
    .map((col) => {
      const field = normalizeUserColumnField(col.field || col.key);
      if (!field || field === "organization_user_id") return null;

      const defaultCol = USER_DEFAULT_COLUMNS.find((c) => c.field === field);

      return {
        ...(defaultCol || {}),
        ...col,
        field,
        key: field,
        label: col.label || labelByField[field] || field,
        visible: col.visible !== false,
        filterable: col.filterable !== false,
        sortable: col.sortable !== false,
      };
    })
    .filter(Boolean);
}

export const formatUserDisplayName = (name) =>
  String(name ?? "")
    .replace(/\s*\n\s*/g, " ")
    .trim() || "-";

export function mapUserRowForDisplay(user) {
  return {
    ...user,
    organization_user_id: user.organization_user_id,
    application_user_full_name: formatUserDisplayName(
      user.user_name ??
        user.full_name ??
        user?.applicationuser?.[0]?.full_name
    ),
    application_user_email:
      user.email ?? user?.applicationuser?.[0]?.email ?? "-",
    application_user_phone:
      user.phone ?? user?.applicationuser?.[0]?.phone_number ?? "-",
    application_user_role_id_name:
      user.user_role ??
      user.application_user_role ??
      user?.role_assignment?.application_user_role?.user_role_name ??
      "-",
    user_type_name:
      user.user_type ??
      user.user_type_name ??
      user?.user_types?.user_type_name ??
      "-",
    __is_active_for_toggle:
      user?.is_active == 1 ||
      user?.is_active === true ||
      user?.is_active === "1",
    is_active:
      user?.is_active == 1 ||
      user?.is_active === true ||
      user?.is_active === "1"
        ? "Active"
        : "Inactive",
  };
}
